/**
 * LineageList — chronological "Acknowledged" view of The Line.
 *
 * Each row renders the Orchist's:
 *   - Sigil (Glyph component, seeded from glyphSeed)
 *   - Public identity (per their chosen mode)
 *   - Acknowledgment date
 *   - Inviter relationship (when known)
 *   - Private nickname overlay (when the viewer has assigned one)
 *
 * Generic-mode Orchists render with date + glyph only (no name) — the
 * sigil IS the identity in that mode, per sacred/lineage.md doctrine.
 *
 * The First (no inviter) renders with a "self-recognized" line instead
 * of "Acknowledged by", honoring the founding paradox.
 *
 * Free-Will Doctrine (sacred/free-will-and-fluidity.md):
 *   The body of The Line is the active acknowledged. Those who have set
 *   down the recognition are preserved in a quieter section, collapsible.
 *   Their record stays — disowning is a movement in time, not a deletion.
 *
 * Phase G.7.7 — Pagination:
 *   The list now loads incrementally via useLineage's cursor pagination.
 *   Default pageSize=50; "Load more" button at the bottom of each section
 *   when more rows remain on the server. The full count appears in the
 *   header so the surface is honest about how much it's showing.
 *
 * @scale-marker stage-a   — Pagination shipped early as anti-flood
 *   preparation. At Stage A (>1k Orchists), virtualize visible rows with
 *   react-virtual. Sovereign Stewardship will email when this threshold
 *   approaches; see sacred/scale-stewardship.md#stage-a.
 * @scale-marker stage-b   — At ~100k, switch to time-bucketed scroll.
 */

import { useMemo, useState } from 'react';
import { Glyph } from '@/components/Glyph';
import { utcToLocalDateString, resolveTimezone } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import {
  useLineage,
  lineageDisplayString,
  isFirstOrchist,
  splitByDisownment,
  type LineageRow,
} from '@/hooks/useLineage';
import { useNicknames, type NicknameRow } from '@/hooks/useNicknames';
import { useEmailShares, type EmailShareRow } from '@/hooks/useEmailShares';
import {
  OrchistSearchInput,
  matchesOrchistQuery,
} from '@/components/OrchistSearch';
import { AnonymityToggleButton } from '@/components/AnonymityToggleButton';

export function LineageList() {
  const { loading, rows, error, hasMore, loadingMore, loadMore, totalCount, refresh } = useLineage();
  const { contact } = useAuth();
  const { byTargetId: nicknamesByTargetId } = useNicknames();
  const { byOwnerContactId: emailsByOwnerId } = useEmailShares();
  const viewerTz = resolveTimezone(contact?.timezone);

  const { active, disowned } = useMemo(() => splitByDisownment(rows), [rows]);

  // Inviter display map (for "Acknowledged by ..."). Built across ALL rows
  // (active + disowned) since an active Orchist may have been invited by
  // someone who later disowned, and we still want to render their name.
  const inviterDisplay = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      map.set(row.id, lineageDisplayString(row, viewerTz));
    }
    return map;
  }, [rows, viewerTz]);

  const [showDisowned, setShowDisowned] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Apply search filter to active + disowned independently so both
  // sections honor the query.
  const filteredActive = useMemo(() => {
    if (!searchQuery.trim()) return active;
    return active.filter((row) =>
      matchesOrchistQuery(
        row,
        nicknamesByTargetId.get(row.id) ?? null,
        searchQuery,
        viewerTz,
        emailsByOwnerId.get(row.id) ?? null
      )
    );
  }, [active, searchQuery, nicknamesByTargetId, viewerTz]);
  const filteredDisowned = useMemo(() => {
    if (!searchQuery.trim()) return disowned;
    return disowned.filter((row) =>
      matchesOrchistQuery(
        row,
        nicknamesByTargetId.get(row.id) ?? null,
        searchQuery,
        viewerTz,
        emailsByOwnerId.get(row.id) ?? null
      )
    );
  }, [disowned, searchQuery, nicknamesByTargetId, viewerTz]);

  if (loading) {
    return (
      <div className="text-center py-8 text-ink/40 italic font-display">
        The Line gathers...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive text-sm">
        The Line could not be read: {error.message}
      </div>
    );
  }

  if (rows.length === 0 && totalCount === 0) {
    return (
      <div className="text-center py-8 text-ink/40 italic font-display">
        The Line is empty.
      </div>
    );
  }

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="space-y-10">
      {/* Active count — quiet typographic header */}
      <p className="text-center text-[0.7rem] tracking-[0.3em] uppercase text-ink/45">
        {(totalCount ?? active.length) === 1
          ? '1 Orchist'
          : `${(totalCount ?? rows.length).toLocaleString()} Orchists`}{' '}
        in The Line
        {disowned.length > 0 && (
          <>
            <span className="text-ink/25"> &middot; </span>
            <span className="text-ink/35 lowercase tracking-[0.2em]">
              {disowned.length} released
            </span>
          </>
        )}
      </p>

      {/* Inline search */}
      <div className="max-w-md mx-auto">
        <OrchistSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name, nickname, or date..."
          summary={
            isSearching
              ? `${(filteredActive.length + filteredDisowned.length).toLocaleString()} of ${rows.length.toLocaleString()} loaded match`
              : undefined
          }
        />
      </div>

      {filteredActive.length > 0 ? (
        <ol className="space-y-6 list-none pl-0">
          {filteredActive.map((row) => (
            <LineageEntry
              key={row.id}
              row={row}
              inviterDisplay={inviterDisplay}
              viewerTz={viewerTz}
              nickname={nicknamesByTargetId.get(row.id) ?? null}
              emailShare={emailsByOwnerId.get(row.id) ?? null}
              viewerContactId={contact?.id ?? null}
              onAnonymityToggled={refresh}
            />
          ))}
        </ol>
      ) : (
        <p className="text-center py-8 text-ink/40 italic font-display">
          {isSearching
            ? 'No active Orchists match your search.'
            : 'No one currently holds the recognition.'}
        </p>
      )}

      {/* Load more — anti-flood surface for the full population */}
      {hasMore && (
        <div className="text-center pt-4">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="text-xs italic text-ink/55 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink transition-colors disabled:opacity-50"
          >
            {loadingMore ? 'Loading more...' : `Load more (${(totalCount ?? 0) - rows.length} remaining)`}
          </button>
        </div>
      )}

      {/* Free-Will Doctrine: disowned section is real, visible, but quiet */}
      {disowned.length > 0 && (
        <section className="border-t border-ink/10 pt-10">
          <button
            type="button"
            onClick={() => setShowDisowned((v) => !v)}
            aria-expanded={showDisowned}
            className="w-full text-center text-[0.7rem] tracking-[0.3em] uppercase text-ink/40 hover:text-ink/70 transition-colors"
          >
            {showDisowned ? '— hide —' : '+ those who released the recognition'}
          </button>

          {showDisowned && (
            <>
              <p className="text-center text-xs italic font-display text-ink/45 mt-4 mb-8 max-w-md mx-auto leading-relaxed">
                Free-will is the practice. Those listed below have set down the
                Orchist recognition. Their trail remains. The Line never
                un-remembers.
              </p>
              {filteredDisowned.length > 0 ? (
                <ol className="space-y-6 list-none pl-0">
                  {filteredDisowned.map((row) => (
                    <LineageEntry
                      key={row.id}
                      row={row}
                      inviterDisplay={inviterDisplay}
                      viewerTz={viewerTz}
                      nickname={nicknamesByTargetId.get(row.id) ?? null}
                      emailShare={emailsByOwnerId.get(row.id) ?? null}
                      viewerContactId={contact?.id ?? null}
                      onAnonymityToggled={refresh}
                      dimmed
                    />
                  ))}
                </ol>
              ) : (
                <p className="text-center py-4 text-ink/35 italic font-display text-sm">
                  {isSearching ? 'No released Orchists match.' : ''}
                </p>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}

function LineageEntry({
  row,
  inviterDisplay,
  viewerTz,
  nickname,
  emailShare,
  viewerContactId,
  onAnonymityToggled,
  dimmed,
}: {
  row: LineageRow;
  inviterDisplay: Map<string, string>;
  viewerTz: string;
  nickname: NicknameRow | null;
  emailShare: EmailShareRow | null;
  /** The current viewer's Contact id, or null if unauthenticated. */
  viewerContactId: string | null;
  /** Called after a successful anonymity toggle so the parent can refresh. */
  onAnonymityToggled?: () => void;
  dimmed?: boolean;
}) {
  const viewerIsTheInviter = !!viewerContactId && row.invitationSentBy === viewerContactId;
  const display = lineageDisplayString(row, viewerTz);
  const acknowledgedDate = utcToLocalDateString(
    row.invitationAcceptedAt ?? row.invitationConfirmedAt,
    viewerTz
  );
  const disownedDate = utcToLocalDateString(row.orchistDisownedAt, viewerTz);

  const inviterName = row.invitationSentBy ? inviterDisplay.get(row.invitationSentBy) : null;

  // Dimmed disowned rows: ~55% opacity overall, italic body, no glyph saturation.
  const cls = dimmed ? 'opacity-55 italic' : '';

  return (
    <li
      className={`flex items-start gap-5 text-left border-t border-ink/10 pt-6 first:border-t-0 first:pt-0 ${cls}`}
    >
      {row.glyphSeed ? (
        <div className={`flex-shrink-0 ${dimmed ? 'text-ink/55' : 'text-ink/85'}`}>
          <Glyph seed={row.glyphSeed} size={64} label={`${display}'s sigil`} />
        </div>
      ) : (
        <div className="flex-shrink-0 w-16 h-16" aria-hidden />
      )}

      <div className="flex-1 min-w-0 space-y-1">
        <p className={`display text-xl md:text-2xl leading-tight ${dimmed ? 'not-italic' : ''}`}>
          {display}
        </p>

        {/* Private nickname overlay — visible only to the viewer who set it */}
        {nickname && (
          <p className="text-xs text-accent italic font-display not-italic-children">
            <span className="not-italic text-[0.65rem] tracking-[0.2em] uppercase text-ink/40">
              you call them:
            </span>{' '}
            <span className="font-display">{nickname.nickname}</span>
          </p>
        )}

        {/* Shared email overlay — frozen at share-time per Phase J doctrine */}
        {emailShare && (
          <p className="text-xs text-ink/55 italic font-display">
            <span className="not-italic text-[0.65rem] tracking-[0.2em] uppercase text-ink/40">
              email shared with you:
            </span>{' '}
            <span className="font-mono not-italic text-ink/70">{emailShare.sharedEmail}</span>
          </p>
        )}

        {row.publicIdentityMode && row.publicIdentityMode !== 'GENERIC' && acknowledgedDate && (
          <p className="text-xs text-ink/45 tracking-wide not-italic">
            Acknowledged {acknowledgedDate}
          </p>
        )}

        {isFirstOrchist(row) ? (
          <p className="text-xs text-ink/45 italic font-display">
            Self-recognized · The Founding Orchist
          </p>
        ) : row.invitationAnonymous ? (
          <p className="text-xs text-ink/45 italic font-display">
            By an anonymous Orchist
            {viewerIsTheInviter && (
              <>
                {' '}
                <span className="text-ink/25">·</span>{' '}
                <span className="not-italic text-[0.65rem] tracking-[0.2em] uppercase text-ink/35">
                  you
                </span>{' '}
                <AnonymityToggleButton
                  inviteeId={row.id}
                  isAnonymous={true}
                  onSuccess={onAnonymityToggled}
                  compact
                />
              </>
            )}
          </p>
        ) : (
          inviterName && (
            <p className="text-xs text-ink/45 italic font-display">
              Acknowledged by{' '}
              <span className="not-italic">{inviterName}</span>
              {viewerIsTheInviter && (
                <>
                  {' '}
                  <span className="text-ink/25">·</span>{' '}
                  <AnonymityToggleButton
                    inviteeId={row.id}
                    isAnonymous={false}
                    onSuccess={onAnonymityToggled}
                    compact
                  />
                </>
              )}
            </p>
          )
        )}

        {dimmed && disownedDate && (
          <p className="text-xs text-ink/45 not-italic font-display pt-1">
            Released the recognition · {disownedDate}
          </p>
        )}
      </div>
    </li>
  );
}
